/**
 * Main Analyzify SDK class
 *
 * Uses HeadlessConfig accessor to read raw metafield config directly
 * without any transformation.
 */

import type { AnalyzifyOptions, PageContext } from '../types/config';
import type { WindowAnalyzify } from '../types/window-analyzify';
import type { StorefrontProduct, StorefrontCart, StorefrontOrder } from '../types/storefront';
import type { ConsentState } from '../types/events';
import type { HeadlessMetafield } from '../types/headless-config';

import { fetchHeadlessConfig } from './config-fetcher';
import { HeadlessConfig, createHeadlessConfig } from './headless-config';
import { loadScripts } from './script-loader';
import { buildAnalyzifyObject, initializeGlobalFunctions } from './analyzify-builder';
import { installAllShims } from '../shims';
import { EventDispatcher } from '../events/dispatcher';
import { ConsentManager, DEFAULT_CMP_TIMEOUT_MS, buildConsentFields } from '../consent/consent-manager';
import { DataLayerProxy } from './datalayer-proxy';
import { CartSync } from './cart-sync';
import { disableCdnClickDetection } from './click-detection';
import { adaptProduct } from '../adapters/product';
import { adaptCart } from '../adapters/cart';
import { getCollectionDataObject } from '../adapters/collection';
import { getSearchDataObject } from '../adapters/search';
import { adaptCustomerSync } from '../adapters/customer';
import { currentPageKey, variantIdFromUrl } from './page-key';

/**
 * Analyzify SDK for headless Shopify stores
 *
 * @example
 * ```typescript
 * const analyzify = new Analyzify({
 *   shopDomain: 'your-store.myshopify.com',
 *   storefrontToken: 'your-storefront-token',
 * });
 *
 * await analyzify.init();
 *
 * analyzify.setPageContext({ type: 'product', product });
 * analyzify.track.viewItem(product);
 * ```
 */
/**
 * Queued event for pre-init tracking
 */
interface QueuedEvent {
  method: string;
  args: any[];
}

/**
 * Module-level shared init promise.
 * Prevents concurrent full initializations in React Strict Mode
 * where two Analyzify instances start init() simultaneously.
 * The second instance waits for the first to finish, then reuses.
 */
let sharedInitPromise: Promise<void> | null = null;

export class Analyzify {
  private options: AnalyzifyOptions;
  private rawConfig: HeadlessMetafield | null = null;
  private headlessConfig: HeadlessConfig | null = null;
  private dispatcher: EventDispatcher | null = null;
  private consentManager: ConsentManager;
  private initialized = false;
  private platformsInitialized = false; // Track if platforms have been initialized
  private eventQueue: QueuedEvent[] = []; // Queue for pre-init events
  private initialShopifyNativePageViewSent = false; // Ensure first-load Shopify native PAGE_VIEW is dispatched once
  private _shopifyConsentListenerRegistered = false;
  private consentUnsubscribers: Array<() => void> = []; // Disposers for consentManager.onChange subscriptions
  private destroyed = false; // Idempotency guard for destroy()
  private cartSync: CartSync;
  // Page signals held for a provisional (URL-derived) page type, see setPageContext()
  private pendingPageSignals: ReturnType<typeof setTimeout> | null = null;
  private pendingWasPreviouslyTracked = false;

  constructor(options: AnalyzifyOptions) {
    this.options = options;
    this.consentManager = new ConsentManager(
      !!options.cookiebotId,
      options.checkoutRootDomain,
      options.storefrontRootDomain,
      options.storefrontToken
    );
    this.cartSync = new CartSync({
      shopDomain: options.shopDomain,
      storefrontToken: options.storefrontToken,
      writeAttributes: options.cartAttributes !== false,
    });
  }

  /**
   * Queue an event for later processing (used when SDK not yet initialized)
   */
  private queueEvent(method: string, args: any[]): void {
    this.eventQueue.push({ method, args });
    if (this.options.debug) {
      console.log(`[Analyzify] Queued event: ${method} (SDK not yet initialized)`);
    }
  }

  /**
   * Process queued events after initialization
   */
  private processEventQueue(): void {
    if (this.eventQueue.length === 0) return;

    if (this.options.debug) {
      console.log(`[Analyzify] Processing ${this.eventQueue.length} queued events`);
    }

    while (this.eventQueue.length > 0) {
      const event = this.eventQueue.shift();
      if (event && this.dispatcher) {
        try {
          const trackMethod = (this.dispatcher as any)[event.method];
          if (typeof trackMethod === 'function') {
            trackMethod.apply(this.dispatcher, event.args);
          }
        } catch (error) {
          console.warn(`[Analyzify] Error processing queued event ${event.method}:`, error);
        }
      }
    }
  }

  /**
   * Initialize the SDK
   * - Fetches configuration from Storefront API
   * - Installs Shopify shims
   * - Loads CDN scripts
   * - Initializes platforms
   */
  async init(): Promise<void> {
    if (this.initialized) {
      console.warn('Analyzify already initialized');
      return;
    }

    if (typeof window === 'undefined') {
      console.warn('Analyzify can only be initialized in browser environment');
      return;
    }

    // Default consent when no CMP will ask the visitor (fail-open by default,
    // per consentDefaults.timeoutFallback). Keeps canTrack(), the
    // consentSettings on every dataLayer push and Shopify native analytics in
    // line with the CDN scripts, which track freely when consent management is
    // off. A stored decision, a later setConsent() or a Shopify Privacy API
    // update still wins. Applied on every instance — including one that reuses
    // an existing initialization — because each instance has its own state.
    if (!this.consentManager.hasCMP) {
      this.consentManager.applyDefaultConsent(this.options.consentDefaults?.timeoutFallback !== 'deny');
    }

    try {
      // CRITICAL: Check if SDK was already initialized (e.g., React strict mode, multiple instances)
      // CDN scripts store internal references to window.analyzify during initGa4Gads().
      // If we replace window.analyzify, those internal references become stale.
      // Solution: Reuse existing window.analyzify if it was SDK-initialized.
      const existingAnalyzify = (window as any).analyzify;
      if (existingAnalyzify?.__sdk_initialized__) {
        if (this.options.debug) {
          console.log('Analyzify: Reusing existing SDK initialization');
        }
        // Reuse existing analyzify object to preserve CDN internal references
        this.dispatcher = new EventDispatcher(existingAnalyzify);
        this.applyShopifyNativeConfig();
        this.processEventQueue(); // drain events queued before this instance had a dispatcher
        this.initialized = true;
        this.platformsInitialized = existingAnalyzify.__platforms_initialized__ || false;
        this.options.onReady?.();
        return;
      }

      // CRITICAL: Prevent concurrent full initializations (React Strict Mode).
      // In dev mode, Strict Mode double-mounts components, creating two Analyzify
      // instances that both call init() simultaneously. Without this guard, both
      // run the full init path concurrently, causing race conditions.
      // The second instance waits for the first to complete, then reuses.
      if (sharedInitPromise) {
        if (this.options.debug) {
          console.log('Analyzify: Waiting for concurrent init to complete');
        }
        await sharedInitPromise;
        // After waiting, the first init should have completed — reuse
        const existingAfterWait = (window as any).analyzify;
        if (existingAfterWait?.__sdk_initialized__) {
          this.dispatcher = new EventDispatcher(existingAfterWait);
          this.applyShopifyNativeConfig();
          this.processEventQueue(); // drain events queued before this instance had a dispatcher
          this.initialized = true;
          this.platformsInitialized = existingAfterWait.__platforms_initialized__ || false;
          this.options.onReady?.();
          return;
        }
      }

      // Start the full initialization — set shared promise so concurrent callers wait
      const initWork = this.doFullInit();
      sharedInitPromise = initWork;
      await initWork;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error('Analyzify initialization failed:', err);
      this.options.onError?.(err);
      throw err;
    } finally {
      sharedInitPromise = null;
    }
  }

  /**
   * Full initialization logic (extracted for shared promise guard)
   */
  private async doFullInit(): Promise<void> {
      // 1. Get configuration (from options or fetch from Storefront API)
      if (this.options.config) {
        // Use provided config (useful for testing/mocking)
        this.rawConfig = this.options.config;
        if (this.options.debug) {
          console.log('Analyzify: Using provided config');
        }
      } else if (this.options.storefrontToken) {
        // Fetch raw config from Storefront API
        this.rawConfig = await fetchHeadlessConfig(
          this.options.shopDomain,
          this.options.storefrontToken
        );
      } else {
        throw new Error('Either storefrontToken or config must be provided');
      }

      // 2. Create HeadlessConfig accessor
      this.headlessConfig = createHeadlessConfig(this.rawConfig);


      // 3. Install Shopify shims (includes customerPrivacy property watcher)
      installAllShims({
        shopDomain: this.options.shopDomain,
        currency: this.options.currency || this.headlessConfig.str('currency'),
        locale: this.options.locale,
        country: this.options.country,
        storefrontRootDomain: this.options.storefrontRootDomain,
        checkoutRootDomain: this.options.checkoutRootDomain,
        storefrontAccessToken: this.options.storefrontToken,
      });

      // 4. Build window.analyzify object
      const analyzifyObj = buildAnalyzifyObject(
        this.headlessConfig,
        { type: 'other' },
        {
          shopDomain: this.options.shopDomain,
          locale: this.options.locale,
          country: this.options.country,
          currency: this.options.currency || this.headlessConfig.str('currency'),
        }
      );

      // Mark as SDK-initialized to prevent replacement on subsequent init() calls
      (analyzifyObj as any).__sdk_initialized__ = true;
      (window as any).analyzify = analyzifyObj;

      // 4b. Sync consent state that SDK ConsentManager may have received
      // before window.analyzify existed (e.g., visitorConsentCollected during config fetch).
      // Uses spread to preserve builder fields (sale_of_data, wait_for_update).
      // Includes analyzify_consent: an_gtm.js replaces a current_consent that
      // lacks it with its own all-denied default on load.
      analyzifyObj.current_consent = {
        ...analyzifyObj.current_consent,
        ...buildConsentFields(this.consentManager.getGCMState()),
      };
      if (this.consentManager.consentReady && analyzifyObj.consentManager) {
        analyzifyObj.consentManager.consentReady = true;
      }

      // 4c. Set CMP flag on the builder's consentManager so dispatcher knows not to force-grant
      if (this.options.cookiebotId && analyzifyObj.consentManager) {
        (analyzifyObj.consentManager as any).hasCMP = true;
        (analyzifyObj.consentManager as any).cmpTimeout = this.options.consentDefaults?.wait_for_update || DEFAULT_CMP_TIMEOUT_MS;
      }

      // Fail-open/closed fallback preference used by platform-init when a CMP
      // times out or when no CMP is configured. Defaults to 'grant' to preserve
      // historical behavior; set consentDefaults.timeoutFallback: 'deny' to
      // withhold consent until the visitor makes an explicit choice.
      if (analyzifyObj.consentManager) {
        (analyzifyObj.consentManager as any).timeoutFallback =
          this.options.consentDefaults?.timeoutFallback || 'grant';
        // platform-init applies the CMP-timeout / no-CMP fallback to the
        // builder's consent state; mirror it onto the SDK state as well.
        Object.defineProperty(analyzifyObj.consentManager, 'applySdkFallback', {
          value: (granted: boolean) => ConsentManager.applyFallbackToAll(granted),
          configurable: true,
        });
      }

      // 4c. Set SERVERSIDE.shop_domain after object is built
      if (analyzifyObj.properties.SERVERSIDE) {
        analyzifyObj.properties.SERVERSIDE.shop_domain = this.options.shopDomain;
      }

      // 4c. Set external window properties (extension sets these outside window.analyzify)
      (window as any).analyzify_custom_classes = '';
      (window as any).analyzify_measurement_id = this.headlessConfig.str('ga4_id') || '';
      (window as any).analyzify_measurement_id_v3 = this.headlessConfig.str('ga4_id') || '';
      (window as any).collection_sku_list = [];

      // 4d. CRITICAL: Set default global variables BEFORE script loading
      // The script-loader.ts creates `var collection = window.collection || null;` for Klaviyo.
      // If window.collection is null/undefined at that time, the bare `collection` variable
      // gets captured as null in CDN closures, causing errors like:
      // "Cannot read properties of undefined (reading 'map')" when CDN reads collection.products
      // We set proper default objects here so CDN scripts capture valid structures.
      const defaultCollection = {
        id: 0,
        title: '',
        handle: '',
        products: [],
        all_products_count: 0,
        products_count: 0,
      };
      (window as any).collection = defaultCollection;
      (window as any).taxonomy = { category: '', breadcrumbs: [] };
      (window as any).product = null;
      (window as any).variant = null;
      (window as any).cart = { items: [], total_price: 0, item_count: 0 };

      // 4e. Initialize global functions (getClientId, getSessionId, getCookieValue)
      initializeGlobalFunctions();

      // 4f. Install DataLayer proxy for consent injection + event buffering.
      //     Create dataLayer BEFORE loadScripts so script-loader.ts line 148
      //     (`window.dataLayer = window.dataLayer || []`) becomes a no-op.
      //     The proxy must be in place before any CDN scripts push events.
      const shouldBuffer = this.consentManager.hasCMP && !this.consentManager.canTrack();
      (window as any).dataLayer = (window as any).dataLayer || [];
      const dlProxy = new DataLayerProxy(this.consentManager, shouldBuffer);
      dlProxy.install((window as any).dataLayer);

      // 5. Load CDN scripts
      await loadScripts(this.headlessConfig, {
        scriptAttribute: this.options.scriptAttribute,
        gtmContainerId: this.options.gtmContainerId,
        gtmServerUrl: this.options.gtmServerUrl,
        consentDefaults: this.options.consentDefaults,
        cookiebotId: this.options.cookiebotId,
        gtmScriptUrl: this.options.gtmScriptUrl,
        storefrontRootDomain: this.options.storefrontRootDomain,
        checkoutRootDomain: this.options.checkoutRootDomain,
        storefrontAccessToken: this.options.storefrontToken,
      });

      // 5a. Re-install proxy — GTM replaces dataLayer.push during its load with
      //     its own function. We must wrap GTM's push to continue intercepting.
      //     The buffer is preserved on the proxy instance across reinstalls.
      dlProxy.install((window as any).dataLayer);

      // 5a-2. The CDN scripts define their ecommerce click-detection maps on
      //       load. Empty them unless the integrator opted in — explicit SDK
      //       track calls report these events in headless.
      if (!this.options.autoDetectClicks && (window as any).analyzify) {
        disableCdnClickDetection((window as any).analyzify);
      }

      // 5b. CRITICAL: Ensure collection objects have products array AFTER CDN loads
      // CDN's an_analyzify.js creates BOTH analyzify.collection AND window.collection
      // as { id, title, handle } WITHOUT the products array.
      // CDN scripts like GTM/Bing check shopify_template === 'collection' and try to read
      // collection.products.map() - this fails if products is undefined.
      // We MUST ensure products array exists on both objects.
      const analyzifyAfterCDN = (window as any).analyzify;
      if (analyzifyAfterCDN) {
        // Ensure analyzify.collection has products array
        if (!analyzifyAfterCDN.collection || !Array.isArray(analyzifyAfterCDN.collection.products)) {
          analyzifyAfterCDN.collection = {
            id: analyzifyAfterCDN.collection?.id ?? 0,
            title: analyzifyAfterCDN.collection?.title ?? '',
            handle: analyzifyAfterCDN.collection?.handle ?? '',
            products: [],
            all_products_count: 0,
            products_count: 0,
          };
        }
        // Ensure window.collection also has products array (for bare variable references)
        const winCollection = (window as any).collection;
        if (!winCollection || !Array.isArray(winCollection.products)) {
          (window as any).collection = analyzifyAfterCDN.collection;
        }
      }

      // 5c. Wire up DataLayer proxy flush on consent granted.
      //     Buffer stays active while consent is denied — never discarded.
      //     When consent becomes granted (CMP accept, or deny→accept change),
      //     flush all buffered events with _queue suffix.
      this.consentUnsubscribers.push(this.consentManager.onChange(() => {
        if (dlProxy.isBuffering && this.consentManager.canTrack()) {
          dlProxy.flush();
        }
      }));

      // 5d. Keep window.analyzify.current_consent in sync with SDK ConsentManager.
      //     pushGTMPageInfo() reads current_consent directly for SPA navigation.
      this.consentUnsubscribers.push(this.consentManager.onChange((gcmState) => {
        const win = window as any;
        if (win.analyzify) {
          const { wait_for_update: _, ...fields } = gcmState;
          win.analyzify.current_consent = { ...win.analyzify.current_consent, ...fields };
        }
      }));

      // 6. Initialize event dispatcher
      this.dispatcher = new EventDispatcher((window as any).analyzify);

      // 6b. Configure Shopify native analytics if enabled
      this.applyShopifyNativeConfig();

      // 7. Register all page-specific CDN functions for SPA navigation
      // This loops through all page types (product, collection, cart, search) with
      // tracking suppressed to ensure all functions like gaViewItem, gaViewItemList,
      // gaViewCart, gaSearch are defined regardless of the current page type.
      // Required for 100% CDN parity in SPA environments.
      await this.dispatcher.registerCDNFunctions();

      // 8. Platform initialization is DEFERRED until setPageContext is called
      // This ensures page data (template, product, cart, etc.) is available
      // when CDN scripts check template type and trigger automatic events
      // NOTE: If you don't call setPageContext, call initializePlatforms() manually

      this.initialized = true;

      // Process any events that were queued before initialization
      this.processEventQueue();

      if (this.options.debug) {
        console.log('Analyzify initialized successfully');
      }

      this.options.onReady?.();
  }

  /**
   * Set the current page context
   * Call this on page navigation to update tracking context
   *
   * IMPORTANT: This method triggers platform initialization on first call.
   * The initialization is deferred until page context is set to ensure
   * CDN scripts have access to template and page data when they run.
   */
  setPageContext(context: PageContext): void {
    if (!this.initialized) {
      console.warn('Analyzify not initialized. Call init() first.');
      return;
    }

    // Guard against malformed input (JS consumers, mistaken calls) — a public
    // entry point must not throw into the host app.
    if (!context || typeof context.type !== 'string') {
      console.warn('Analyzify.setPageContext: invalid page context (missing type)');
      return;
    }

    const analyzify = (window as any).analyzify as WindowAnalyzify;
    if (!analyzify) return;

    // The virtual page this context belongs to. The key of the last page that
    // got its page-level signals (sh_info, server-side page_view) lives on
    // window.analyzify so every SDK instance that shares the initialization
    // (StrictMode, provider re-mounts) agrees on it.
    const pageKey = currentPageKey();
    // A new term on a search page is a new results page (same pathname)
    const searchTerm = context.type === 'search' && context.search ? (context.search.term ?? null) : undefined;
    const lastSearchTerm = (analyzify as any).__last_search_term__ ?? null;
    const isNewPage = this.platformsInitialized && (
      pageKey !== (analyzify as any).__last_page_key__ ||
      (searchTerm != null && lastSearchTerm != null && searchTerm !== lastSearchTerm)
    );

    // Fresh event IDs for a new page (the init call resets them before the
    // CDN's landing-page events run). Calls for the same page keep the IDs, so
    // a route tracker and a page tracker calling setPageContext for one page
    // don't split its events across two IDs.
    if ((isNewPage || !this.platformsInitialized) && typeof (analyzify as any).eventId?.resetAll === 'function') {
      (analyzify as any).eventId.resetAll();
    }

    // Update template
    analyzify.shopify_template = context.type;
    analyzify.content_type = context.type.charAt(0).toUpperCase() + context.type.slice(1);

    // Update page-specific data
    if (context.product) {
      const adapted = adaptProduct(context.product, context.variantId ?? variantIdFromUrl());
      analyzify.product = adapted.product;
      (analyzify as any).getProductObj = adapted;

      // Set additional product context fields expected by extension
      const variant = adapted.variant;
      const priceInDollars = variant.price / 100;
      (analyzify as any).product.value = priceInDollars;
      (analyzify as any).product.revenue_value = priceInDollars;
      (analyzify as any).product.variantInput = String(variant.id);
      (analyzify as any).product.variantAvailable = variant.available;
      (analyzify as any).product.variant_title = variant.title;

      // Set collection context if available
      if (adapted.collection) {
        (analyzify as any).product.collectionTitle = adapted.collection.title;
        (analyzify as any).product.collectionId = String(adapted.collection.id);
        (analyzify as any).product.collectionHandle = adapted.collection.handle;
      }
    }

    if (context.cart) {
      analyzify.detectedCart = adaptCart(context.cart);
      this.cartSync?.sync(context.cart);
    }

    if (context.collection) {
      const collectionData = getCollectionDataObject(context.collection);

      // CRITICAL: Mutate the EXISTING window.collection object instead of replacing it
      // The script-loader.ts creates `var collection = window.collection;` at load time.
      // CDN scripts capture this bare variable in closures. If we replace window.collection
      // with a new object, the CDN closures still have the old reference.
      // By MUTATING the existing object, CDN closures see the updated data.
      const existingCollection = (window as any).collection;
      if (existingCollection && typeof existingCollection === 'object') {
        // Clear existing properties
        Object.keys(existingCollection).forEach((key) => {
          delete existingCollection[key];
        });
        // Copy new properties to the same object reference
        Object.assign(existingCollection, collectionData.collection);
        // Point ALL collection references to the SAME object
        (analyzify as any).collection = existingCollection;
        // Update getCollectionObj to also point to the same collection object
        collectionData.collection = existingCollection;
        (analyzify as any).getCollectionObj = collectionData;
      } else {
        // Fallback: no existing collection, set new references
        (window as any).collection = collectionData.collection;
        (analyzify as any).collection = collectionData.collection;
        (analyzify as any).getCollectionObj = collectionData;
      }

      // Populate window.collection_sku_list for product position tracking
      // Used by GetClickedProductPosition to determine product index in collections
      (window as any).collection_sku_list = collectionData.collection_sku_list || [];
    }

    if (context.search) {
      const searchData = getSearchDataObject(
        context.search.term,
        context.search.results,
        context.search.resultsCount
      );
      (analyzify as any).getSearchObj = searchData;

      // Also set top-level search properties (extension reads these directly)
      (analyzify as any).term = searchData.term;
      (analyzify as any).products = searchData.products;
      (analyzify as any).resultsCount = searchData.resultsCount;
      (analyzify as any).search = searchData;
    }

    if (context.customer) {
      analyzify.shopify_customer = adaptCustomerSync(context.customer);
    }

    // Initialize platforms on first setPageContext call
    // This ensures page data is available when CDN scripts run
    // IMPORTANT: Only initialize if we have the required data for data-dependent page types
    // Otherwise, wait for a subsequent setPageContext call with the full data
    const shouldInitialize = this.shouldInitializeForPageType(context);

    if (!this.platformsInitialized && this.dispatcher && shouldInitialize) {
      this.platformsInitialized = true;
      // Mark on window.analyzify so other SDK instances know platforms are initialized
      (analyzify as any).__platforms_initialized__ = true;
      // Platform init makes the CDN fire the landing page's sh_info, server-side
      // page_view and page-level view event. Record the landing page so later
      // setPageContext() calls for it (page trackers mounting after the route
      // tracker) don't push those signals a second time — and so the first real
      // navigation is tracked normally.
      (analyzify as any).__last_page_key__ = pageKey;
      (analyzify as any).__last_search_term__ = searchTerm ?? null;
      // Skip only the explicit view call that duplicates the landing page's
      // CDN auto-fire (e.g. ProductTracker's viewItem on a landing product page).
      this.dispatcher.suppressNextPageView(context.type, pageKey);
      this.dispatcher
        .attemptInitialization()
        .then(() => {
          this.dispatchInitialShopifyNativePageViewOnce();
        })
        .catch((err) => {
          console.error('Platform initialization failed:', err);
        });
    } else if (isNewPage && this.dispatcher) {
      // SPA navigation to a new page. CDN's gtmInitializeAndTrackPage() only
      // pushes sh_info once (guarded by gtmInitiated), but GTM Trigger Groups
      // (Consent + sh_info) need sh_info on every virtual page.
      //
      // The page's signals go out on the first setPageContext() for the page,
      // before the page's own ecommerce events. With a provisional type (the
      // route tracker's, derived from the URL) they are held until the end of
      // the task, so a page tracker calling for the same page in this task
      // provides the type; otherwise they go out synchronously.
      const wasPreviouslyTracked = !!(analyzify as any).__last_page_key__;
      (analyzify as any).__last_page_key__ = pageKey;
      (analyzify as any).__last_search_term__ = searchTerm ?? null;
      this.cancelPendingPageSignals();
      if (context.provisional) {
        this.pendingWasPreviouslyTracked = wasPreviouslyTracked;
        this.pendingPageSignals = setTimeout(() => {
          this.pendingPageSignals = null;
          this.emitPageSignals(wasPreviouslyTracked);
        }, 0);
      } else {
        this.emitPageSignals(wasPreviouslyTracked);
      }
    } else if (this.platformsInitialized && this.dispatcher) {
      // Same page: update the page data (done above). A page tracker arriving
      // while the route tracker's provisional signals are still held sends
      // them now, with its type.
      if (searchTerm != null) {
        (analyzify as any).__last_search_term__ = searchTerm;
      }
      if (this.pendingPageSignals && !context.provisional) {
        this.cancelPendingPageSignals();
        this.emitPageSignals(this.pendingWasPreviouslyTracked);
      }
    }
  }

  private cancelPendingPageSignals(): void {
    if (this.pendingPageSignals) {
      clearTimeout(this.pendingPageSignals);
      this.pendingPageSignals = null;
    }
  }

  /**
   * The page-level signals of a new virtual page: sh_info + consentUpdate to
   * the GTM dataLayer, the server-side page_view and a cart-upsert check.
   */
  private emitPageSignals(wasPreviouslyTracked: boolean): void {
    const analyzify = (window as any).analyzify;
    if (!analyzify || !this.dispatcher) return;
    // On a consent-managed store the CDN defers its GTM page init
    // (landing sh_info + page event) until consent is granted. From here
    // on the SDK pushes every page's GTM events itself, so mark that init
    // as done: left pending, it would fire for whatever page is open when
    // consent arrives — a page the SDK has already tracked. The landing
    // page's deferred GTM events are dropped, as on a Liquid store when the
    // visitor leaves the page before consenting.
    if (analyzify.properties?.GTM?.status && !analyzify.gtmInitiated) {
      analyzify.gtmInitiated = true;
    }
    this.dispatcher.pushGTMPageInfo();
    // Signal to CDN whether this is a same-domain navigation.
    // document.referrer is stale in SPA (never updates on pushState),
    // so the CDN can't compute this itself.
    analyzify._isSameDomainNavigation = wasPreviouslyTracked;
    this.dispatcher.trackPageView();
    this.dispatcher.triggerCartUpsertCheck();
  }

  /**
   * Check if we should initialize platforms for the given page context
   * For data-dependent page types (product, collection, cart, search),
   * we only initialize if the required data is provided
   */
  private shouldInitializeForPageType(context: PageContext): boolean {
    switch (context.type) {
      case 'product':
        // Product pages require product data for CDN auto-tracking
        return !!context.product;
      case 'collection':
        // Collection pages require collection data
        return !!context.collection;
      case 'cart':
        // Cart pages require cart data
        return !!context.cart;
      case 'search':
        // Search pages require search data
        return !!context.search;
      default:
        // Other page types (index, page, other) don't require data
        return true;
    }
  }

  /**
   * Dispatch Shopify native first-load PAGE_VIEW exactly once.
   * Covers both initialization-only flows and double setPageContext() flows.
   */
  private dispatchInitialShopifyNativePageViewOnce(): void {
    if (this.initialShopifyNativePageViewSent) return;
    if (!this.options.shopifyNativeAnalytics || !this.dispatcher) return;
    this.initialShopifyNativePageViewSent = true;
    this.dispatcher.fireShopifyNativePageView();
  }

  /**
   * Manually initialize platforms without setting page context
   * Use this if you need to initialize before page data is available
   */
  async initializePlatforms(): Promise<void> {
    if (!this.initialized) {
      console.warn('Analyzify not initialized. Call init() first.');
      return;
    }

    if (this.platformsInitialized) {
      console.warn('Platforms already initialized');
      return;
    }

    if (this.dispatcher) {
      this.platformsInitialized = true;
      const analyzify = (window as any).analyzify;
      if (analyzify) analyzify.__platforms_initialized__ = true;
      await this.dispatcher.attemptInitialization();
    }
  }

  /**
   * Apply Shopify native analytics config to the current dispatcher.
   * Called from all init paths to avoid copy-paste. The consent onChange
   * listener is registered at most once (guarded by flag) so Strict Mode
   * double-mounts don't accumulate duplicate listeners.
   */
  private applyShopifyNativeConfig(): void {
    if (!this.options.shopifyNativeAnalytics || !this.dispatcher) return;

    const shopifyOpts = this.options.shopifyNativeAnalytics;
    const consent = this.consentManager.getConsent();
    this.dispatcher.setShopifyNativeConfig({
      shopId: shopifyOpts.shopId,
      currency: shopifyOpts.currency
        || this.options.currency
        || this.headlessConfig?.str('currency')
        || 'USD',
      acceptedLanguage: shopifyOpts.acceptedLanguage
        || this.options.locale?.split('-')[0]?.toUpperCase()
        || 'EN',
      hasUserConsent: this.consentManager.canTrack(),
      storefrontId: shopifyOpts.storefrontId,
      shopDomain: this.options.shopDomain,
      analyticsAllowed: consent.analytics,
      marketingAllowed: consent.marketing,
      gdprEnforced: shopifyOpts.gdprEnforced,
      ccpaEnforced: shopifyOpts.ccpaEnforced,
      saleOfDataAllowed: shopifyOpts.saleOfDataAllowed,
    });

    if (this.options.debug) {
      console.log('[Analyzify] Shopify native analytics configured:', shopifyOpts.shopId);
    }

    if (!this._shopifyConsentListenerRegistered) {
      this._shopifyConsentListenerRegistered = true;
      this.consentUnsubscribers.push(this.consentManager.onChange(() => {
        const consent = this.consentManager.getConsent();
        this.dispatcher?.updateShopifyNativeConsent(
          this.consentManager.canTrack(),
          consent.analytics,
          consent.marketing,
        );
      }));
    }
  }

  /**
   * Event tracking methods
   * Events are queued if called before SDK initialization completes
   */
  track = {
    viewItem: (product: StorefrontProduct, variantId?: string): void => {
      if (this.dispatcher) {
        this.dispatcher.trackViewItem(product, variantId);
      } else {
        this.queueEvent('trackViewItem', [product, variantId]);
      }
    },

    viewItemList: (
      products: StorefrontProduct[],
      listName: string,
      listId?: string
    ): void => {
      if (this.dispatcher) {
        this.dispatcher.trackViewItemList(products, listName, listId);
      } else {
        this.queueEvent('trackViewItemList', [products, listName, listId]);
      }
    },

    selectItem: (
      product: StorefrontProduct,
      listName: string,
      index?: number,
      listId?: string | number
    ): void => {
      if (this.dispatcher) {
        this.dispatcher.trackSelectItem(product, listName, index, listId);
      } else {
        this.queueEvent('trackSelectItem', [product, listName, index, listId]);
      }
    },

    addToCart: (
      product: StorefrontProduct,
      quantity: number,
      cart: StorefrontCart,
      variantId?: string
    ): void => {
      this.cartSync.sync(cart);
      if (this.dispatcher) {
        this.dispatcher.trackAddToCart(product, quantity, cart, variantId);
      } else {
        this.queueEvent('trackAddToCart', [product, quantity, cart, variantId]);
      }
    },

    removeFromCart: (
      product: StorefrontProduct,
      quantity: number,
      cart: StorefrontCart,
      variantId?: string
    ): void => {
      this.cartSync.sync(cart);
      if (this.dispatcher) {
        this.dispatcher.trackRemoveFromCart(product, quantity, cart, variantId);
      } else {
        this.queueEvent('trackRemoveFromCart', [product, quantity, cart, variantId]);
      }
    },

    viewCart: (cart: StorefrontCart): void => {
      this.cartSync.sync(cart);
      if (this.dispatcher) {
        this.dispatcher.trackViewCart(cart);
      } else {
        this.queueEvent('trackViewCart', [cart]);
      }
    },

    beginCheckout: (cart: StorefrontCart): void => {
      this.cartSync.sync(cart);
      if (this.dispatcher) {
        this.dispatcher.trackBeginCheckout(cart);
      } else {
        this.queueEvent('trackBeginCheckout', [cart]);
      }
    },

    purchase: (order: StorefrontOrder): void => {
      if (this.dispatcher) {
        this.dispatcher.trackPurchase(order);
      } else {
        this.queueEvent('trackPurchase', [order]);
      }
    },

    search: (
      term: string,
      results: StorefrontProduct[],
      resultsCount: number
    ): void => {
      if (this.dispatcher) {
        this.dispatcher.trackSearch(term, results, resultsCount);
      } else {
        this.queueEvent('trackSearch', [term, results, resultsCount]);
      }
    },

    generateLead: (formData: Record<string, any>): void => {
      if (this.dispatcher) {
        this.dispatcher.trackGenerateLead(formData);
      } else {
        this.queueEvent('trackGenerateLead', [formData]);
      }
    },

    addToWishlist: (product: StorefrontProduct, variantId?: string): void => {
      if (this.dispatcher) {
        this.dispatcher.trackAddToWishlist(product, variantId);
      } else {
        this.queueEvent('trackAddToWishlist', [product, variantId]);
      }
    },

    variantChange: (product: StorefrontProduct, variantId: string): void => {
      try {
        if (this.dispatcher) {
          this.dispatcher.trackVariantChange(product, variantId);
        } else {
          this.queueEvent('trackVariantChange', [product, variantId]);
        }
      } catch {
        // Silently catch CDN errors (TypeError/ReferenceError expected in headless SPA)
      }
    },

    homepage: (): void => {
      if (this.dispatcher) {
        this.dispatcher.trackHomepage();
      } else {
        this.queueEvent('trackHomepage', []);
      }
    },

    navClick: (title: string, url: string, position: string): void => {
      if (this.dispatcher) {
        this.dispatcher.trackNavClick(title, url, position);
      } else {
        this.queueEvent('trackNavClick', [title, url, position]);
      }
    },
  };

  /**
   * Tell the SDK about the current cart (call on every cart change).
   *
   * Track calls that take a cart do this automatically. Call it yourself for
   * cart changes that aren't tracked (e.g. a cart restored on page load) so
   * the CDN scripts see the cart: its token is written to the `cart` cookie
   * and window.analyzify.cart_id, /cart.js answers with it, and the
   * attribution attributes are written onto it (see options.cartAttributes).
   */
  setCart(cart: StorefrontCart | null | undefined): void {
    this.cartSync.sync(cart);
  }

  /**
   * Consent management methods
   */
  consent = {
    setConsent: (state: Partial<ConsentState>): void => {
      this.consentManager.setConsent(state);
    },

    getConsent: (): ConsentState => {
      return this.consentManager.getConsent();
    },

    canTrack: (): boolean => {
      return this.consentManager.canTrack();
    },

    onChange: (callback: () => void): (() => void) => {
      return this.consentManager.onChange(() => callback());
    },
  };

  /**
   * Check if SDK is initialized
   */
  get isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Get raw headless config metafield
   */
  getRawConfig(): HeadlessMetafield | null {
    return this.rawConfig;
  }

  /**
   * Get HeadlessConfig accessor instance
   */
  getConfig(): HeadlessConfig | null {
    return this.headlessConfig;
  }

  /**
   * Tear down this SDK instance's listeners and consent subscriptions.
   *
   * Fixes listener/subscription leaks on unmount — important for React
   * StrictMode double-mounts and SPA re-mounts where instances are created and
   * discarded repeatedly. Idempotent and never throws.
   *
   * NOTE: Intentionally does NOT remove the shared window.analyzify global.
   * Other instances (StrictMode reuse path) and SSR-hydration may still rely on
   * it. Only this instance's own ConsentManager listeners, consent onChange
   * subscriptions, and pending timers are cleaned up.
   */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;

    // Remove this instance's ConsentManager window/document listeners and
    // clear its callbacks. Present on every instance (setupEventListeners runs
    // in the ConsentManager constructor), including reuse-path instances.
    try {
      this.consentManager.destroy();
    } catch (error) {
      console.warn('[Analyzify] Error during consent manager teardown:', error);
    }

    // Invoke all captured consent onChange unsubscribe fns.
    for (const unsubscribe of this.consentUnsubscribers) {
      try {
        unsubscribe();
      } catch {
        // Ignore teardown errors — destroy() must never throw.
      }
    }
    this.consentUnsubscribers = [];

    this.cancelPendingPageSignals();
  }
}
